import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { nextBirthday, countdownLabel } from '../../utils/dates';

const EVENT_TYPES = [
    { value: 'custom', label: '📅 Custom' },
    { value: 'birthday', label: '🎂 Birthday' },
    { value: 'christmas', label: '🎄 Christmas' },
    { value: 'anniversary', label: '💍 Anniversary' },
    { value: 'graduation', label: '🎓 Graduation' },
    { value: 'wedding', label: '💒 Wedding' },
    { value: 'valentines', label: "💝 Valentine's Day" },
    { value: 'gotcha_day', label: '🐾 Gotcha Day' },
];

const BLANK_EVENT = { name: '', event_type: 'custom', event_date: '', user_id: '' };

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

function formatEventType(type) {
    return EVENT_TYPES.find((t) => t.value === type)?.label || type;
}

// Parse a "YYYY-MM-DD" date string as a LOCAL date (avoids timezone drift).
function parseLocalDate(dateStr) {
    const [y, m, d] = String(dateStr).split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d);
}

export default function EventsTab({ group, events, members, pets = [], user, onEventsChanged, onViewPersonWishes, notify }) {
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(BLANK_EVENT);
    const navigate = useNavigate();

    const myMembership = members.find((m) => m.id === user?.id);
    const isAdmin = myMembership?.pivot?.role === 'admin';

    // Birthdays derived from members' profiles, soonest first.
    const birthdays = members
        .map((m) => ({ member: m, bday: nextBirthday(m.birthday) }))
        .filter((b) => b.bday)
        .sort((a, b) => a.bday.daysUntil - b.bday.daysUntil);

    // Pet celebrations: each pet contributes its birthday and/or its gotcha day
    // (adoption anniversary). Reuses the same year-agnostic `nextBirthday` helper
    // as member birthdays, so the countdowns line up. Soonest first.
    const petCelebrations = pets
        .flatMap((pet) => {
            const occasions = [];
            const bday = nextBirthday(pet.birthday);
            if (bday) occasions.push({ pet, kind: 'birthday', next: bday });
            const gotcha = nextBirthday(pet.gotcha_day);
            if (gotcha) occasions.push({ pet, kind: 'gotcha_day', next: gotcha });
            return occasions;
        })
        .sort((a, b) => a.next.daysUntil - b.next.daysUntil);

    const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

    const openCreate = () => {
        setEditingId(null);
        setForm(BLANK_EVENT);
        setShowForm(true);
    };

    const openEdit = (event) => {
        setEditingId(event.id);
        setForm({
            name: event.name || '',
            event_type: event.event_type || 'custom',
            event_date: String(event.event_date).split('T')[0].slice(0, 10),
            user_id: event.user_id ?? '',
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm(BLANK_EVENT);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = { ...form, user_id: form.user_id === '' ? null : form.user_id };
        try {
            if (editingId) {
                await api.put(`/events/${editingId}`, payload);
                notify('success', 'Event updated.');
            } else {
                await api.post(`/groups/${group.id}/events`, payload);
                notify('success', 'Event created.');
            }
            closeForm();
            onEventsChanged();
        } catch (err) {
            notify('error', errText(err, 'Could not save the event.'));
        }
    };

    const handleDelete = async (event) => {
        try {
            await api.delete(`/events/${event.id}`);
            notify('success', 'Event deleted.');
            onEventsChanged();
        } catch (err) {
            const data = err.response?.data;
            // Backend returns a 422 with orphan info when wishes are attached.
            if (data?.errors?.can_orphan || data?.can_orphan) {
                const canOrphan = data.can_orphan ?? data.errors?.can_orphan;
                const eventPassed = data.event_passed ?? data.errors?.event_passed;
                if (canOrphan && eventPassed) {
                    if (window.confirm('This past event has wishes attached. Delete the event and keep those wishes as generic?')) {
                        try {
                            await api.delete(`/events/${event.id}/orphan-wishes`);
                            notify('success', 'Event deleted; wishes kept.');
                            onEventsChanged();
                            return;
                        } catch (err2) {
                            notify('error', errText(err2, 'Could not delete the event.'));
                            return;
                        }
                    }
                    return;
                }
            }
            notify('error', errText(err, 'Could not delete the event.'));
        }
    };

    const sorted = [...events].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    const today = new Date(new Date().toDateString());

    return (
        <div>
            {/* Birthdays — pulled from members' profiles. Each links to that
                person's wish list so you can grab a gift. */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">🎂 Birthdays</h2>
                {birthdays.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm p-4 text-sm text-gray-500">
                        No birthdays yet — members can add theirs in their profile.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {birthdays.map(({ member, bday }) => (
                            <div key={member.id} className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <p className="font-medium text-gray-900">
                                        {member.name} {member.id === user?.id && <span className="text-xs text-gray-400">(You)</span>}
                                    </p>
                                    <span className="text-sm text-gray-500">{bday.label}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${bday.isToday ? 'bg-green-100 text-green-700' : 'bg-pink-100 text-pink-700'}`}>
                                        {countdownLabel(bday.daysUntil)}
                                    </span>
                                </div>
                                {member.id !== user?.id && onViewPersonWishes && (
                                    <button
                                        onClick={() => onViewPersonWishes(member.id)}
                                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                        See their wishes →
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Pet celebrations — birthdays + gotcha days for the group's pets.
                Only shown when at least one pet has a date set. */}
            {petCelebrations.length > 0 && (
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">🐾 Pet celebrations</h2>
                    <div className="space-y-2">
                        {petCelebrations.map(({ pet, kind, next }) => (
                            <div key={`${pet.id}-${kind}`} className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <p className="font-medium text-gray-900">
                                        {pet.name}
                                        <span className="text-gray-500 font-normal">
                                            {kind === 'gotcha_day' ? "'s gotcha day" : "'s birthday"}
                                        </span>
                                    </p>
                                    <span className="text-sm text-gray-500">
                                        {kind === 'gotcha_day' ? '🏡' : '🎂'} {next.label}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${next.isToday ? 'bg-green-100 text-green-700' : 'bg-teal-100 text-teal-700'}`}>
                                        {countdownLabel(next.daysUntil)}
                                    </span>
                                </div>
                                <button
                                    onClick={() => navigate(`/pet/${pet.id}`)}
                                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    See their wishes →
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Group Events</h2>
                <button
                    onClick={openCreate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                    + Add Event
                </button>
            </div>

            {sorted.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">No events yet.</div>
            ) : (
                <div className="space-y-2">
                    {sorted.map((event) => {
                        const date = parseLocalDate(String(event.event_date).slice(0, 10));
                        const daysUntil = Math.round((date - today) / (1000 * 60 * 60 * 24));
                        const isPast = daysUntil < 0;
                        const isToday = daysUntil === 0;

                        return (
                            <div key={event.id} className={`bg-white rounded-lg shadow-sm p-4 flex items-center justify-between ${isPast ? 'opacity-60' : ''}`}>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium text-gray-900">{event.name}</p>
                                        <span className="text-xs text-gray-500">{formatEventType(event.event_type)}</span>
                                        {isToday && <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Today!</span>}
                                        {!isPast && !isToday && daysUntil <= 30 && (
                                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">in {daysUntil} day{daysUntil !== 1 ? 's' : ''}</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500">{date.toLocaleDateString()}</p>
                                    {event.user && <p className="text-xs text-gray-400 mt-0.5">For {event.user.name}</p>}
                                </div>
                                <div className="flex gap-3">
                                    <button onClick={() => openEdit(event)} className="text-sm text-gray-600 hover:text-gray-900">Edit</button>
                                    {isAdmin && (
                                        <button onClick={() => handleDelete(event)} className="text-sm text-red-600 hover:text-red-700">Delete</button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit Event' : 'Add Event'}</h3>
                        <form onSubmit={handleSubmit} className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input type="text" required value={form.name} onChange={(e) => setField('name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                                <select value={form.event_type} onChange={(e) => setField('event_type', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                                    {EVENT_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                                <input type="date" required value={form.event_date} onChange={(e) => setField('event_date', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">For member (optional)</label>
                                <select value={form.user_id} onChange={(e) => setField('user_id', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                                    <option value="">— whole group —</option>
                                    {members.map((m) => (
                                        <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-2 justify-end pt-2">
                                <button type="button" onClick={closeForm} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">{editingId ? 'Save' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
